import React from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import LibraryMusicRoundedIcon from '@mui/icons-material/LibraryMusicRounded'
import QueueMusicRoundedIcon from '@mui/icons-material/QueueMusicRounded'
import DoDisturbRoundedIcon from '@mui/icons-material/DoDisturbRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import useStationStore from '../../store/station'
import { useMe } from '../../app/MeContext'
import {
  convertSingleVideo,
  fetchPlaylist,
  getCreditsStatus,
  getHistory,
  getPlaylistHistory,
  pollPlaylistJob,
  startPlaylistJob,
  type ConvertResponse,
  type PlaylistFetchResponse,
  type PlaylistHistoryJob,
  type PlaylistJobStatus,
  type HistoryEntry,
  type YtCreditsStatus,
} from '../../api/ytmp3'
import './YoutubeMp3Downloader.css'

type TabValue = 'single' | 'playlist'

type CombinedHistoryRow =
  | { kind: 'single'; entry: HistoryEntry; timestamp: number }
  | { kind: 'playlist'; job: PlaylistHistoryJob; timestamp: number }

const DEFAULT_BITRATE = 128

const HISTORY_STATION_CACHE = 'rpa_ytmp3_history_station'

const getStoredHistoryStationId = (): number | null => {
  if (typeof window === 'undefined') {
    return null
  }
  const stored = localStorage.getItem(HISTORY_STATION_CACHE)
  const value = stored ? Number(stored) : null
  return value && !Number.isNaN(value) ? value : null
}

const setStoredHistoryStationId = (id: number) => {
  if (typeof window === 'undefined') {
    return
  }
  localStorage.setItem(HISTORY_STATION_CACHE, String(id))
}

const YoutubeMp3Downloader: React.FC = () => {
  const me = useMe()
  const stations = me.stations ?? []
  const activeStation = useStationStore((state) => state.activeStation)

  const [tab, setTab] = React.useState<TabValue>('single')
  const stationId = activeStation?.id ?? stations[0]?.id ?? null
  const [url, setUrl] = React.useState('')
  const [creditsStatus, setCreditsStatus] = React.useState<YtCreditsStatus | null>(null)
  const [creditsLoading, setCreditsLoading] = React.useState(false)
  const [result, setResult] = React.useState<ConvertResponse | null>(null)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const singleDownloadFilename = React.useMemo(
    () =>
      buildDownloadFilename({
        title: result?.conversion?.title ?? '',
        id: result?.conversion?.video_id ?? '',
      }),
    [result?.conversion?.title, result?.conversion?.video_id],
  )

  const [playlistUrl, setPlaylistUrl] = React.useState('')
  const [playlistError, setPlaylistError] = React.useState<string | null>(null)
  const [playlistLoading, setPlaylistLoading] = React.useState(false)
  const [playlistData, setPlaylistData] = React.useState<PlaylistFetchResponse | null>(null)
  const [playlistModalOpen, setPlaylistModalOpen] = React.useState(false)
  const [playlistSelections, setPlaylistSelections] = React.useState<Record<string, boolean>>({})
  const [playlistJobId, setPlaylistJobId] = React.useState<string | null>(null)
  const [playlistJobStatus, setPlaylistJobStatus] = React.useState<PlaylistJobStatus | null>(null)
  const [jobPlaylistInfo, setJobPlaylistInfo] = React.useState<PlaylistFetchResponse['playlist'] | null>(null)
  const [jobTrackThumbs, setJobTrackThumbs] = React.useState<Record<string, string | null>>({})
  const [playlistJobError, setPlaylistJobError] = React.useState<string | null>(null)
  const [playlistProcessing, setPlaylistProcessing] = React.useState(false)
  const [historyItems, setHistoryItems] = React.useState<HistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = React.useState(false)
  const [historyError, setHistoryError] = React.useState<string | null>(null)
  const [historyCursor, setHistoryCursor] = React.useState<number | null>(null)
  const [historyHasMore, setHistoryHasMore] = React.useState(false)
  const [manualHistoryEntry, setManualHistoryEntry] = React.useState<HistoryEntry | null>(null)
  const [playlistHistory, setPlaylistHistory] = React.useState<PlaylistHistoryJob[]>([])
  const [playlistHistoryLoading, setPlaylistHistoryLoading] = React.useState(false)
  const [playlistHistoryError, setPlaylistHistoryError] = React.useState<string | null>(null)
  const [playlistHistoryCursor, setPlaylistHistoryCursor] = React.useState<number | null>(null)
  const [playlistHistoryHasMore, setPlaylistHistoryHasMore] = React.useState(false)
  const [expandedPlaylists, setExpandedPlaylists] = React.useState<Record<number, boolean>>({})

  const maxFileSizeMb = creditsStatus?.max_file_size_mb ?? 20
  const downloadAnchorRef = React.useRef<HTMLAnchorElement | null>(null)
  const lastDownloadedUrlRef = React.useRef<string | null>(null)
  const calculatedBitrate = creditsStatus?.default_bitrate ?? DEFAULT_BITRATE

  React.useEffect(() => {
    const fileUrl = result?.conversion?.file_url
    if (!fileUrl) {
      lastDownloadedUrlRef.current = null
      return
    }

    if (lastDownloadedUrlRef.current === fileUrl) {
      return
    }

    lastDownloadedUrlRef.current = fileUrl
    downloadAnchorRef.current?.click()
  }, [result?.conversion?.file_url])

  React.useEffect(() => {
    if (!stationId) {
      setCreditsStatus(null)
      return
    }

    let cancelled = false
    setCreditsLoading(true)

    getCreditsStatus(stationId)
      .then((status) => {
        if (!cancelled) {
          setCreditsStatus(status)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to load credit status.'
          setError(message)
          setCreditsStatus(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCreditsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [stationId, manualHistoryEntry])

  const appendManualHistoryEntry = React.useCallback(
    (conversion: ConversionResult) => {
      const entry: HistoryEntry = {
        id: conversion.attachment_id ?? Date.now(),
        video_id: conversion.video_id,
        title: conversion.title ?? conversion.video_id,
        outcome: conversion.cached ? 'cached' : 'success',
        created_at: new Date().toISOString(),
        duration_sec: conversion.duration_sec ?? null,
        actual_size_bytes: conversion.filesize_bytes ?? null,
        estimated_size_bytes: conversion.filesize_bytes ?? null,
        bitrate: conversion.bitrate ?? calculatedBitrate,
        file_url: conversion.file_url ?? null,
        user: me.user ? { id: me.user.id, name: me.user.name } : null,
      }
      setManualHistoryEntry(entry)
      setHistoryItems((prev) => {
        const filtered = prev.filter((item) => item.file_url !== entry.file_url)
        return [entry, ...filtered]
      })
    },
    [calculatedBitrate, me.user],
  )

  const historyStationId = React.useMemo(() => {
    const storedStation = getStoredHistoryStationId()
    return stationId ?? storedStation ?? stations[0]?.id ?? null
  }, [stationId, stations])

  const loadHistoryInitial = React.useCallback(async () => {
    const targetStation = historyStationId

    if (!targetStation) {
      return
    }

    setHistoryLoading(true)
    setHistoryError(null)

    try {
      const response = await getHistory(targetStation)
      const merged = [...response.items]
      if (manualHistoryEntry && !merged.some((item) => item.file_url === manualHistoryEntry.file_url)) {
        merged.unshift(manualHistoryEntry)
      }
      setHistoryItems(merged)
      setHistoryCursor(response.next_cursor ?? null)
      setHistoryHasMore(Boolean(response.next_cursor))
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to load history.'
      setHistoryError(message)
      setHistoryItems([])
      setHistoryCursor(null)
      setHistoryHasMore(false)
    } finally {
      setHistoryLoading(false)
    }
  }, [historyStationId, manualHistoryEntry])

const loadHistoryMore = React.useCallback(async () => {
  const targetStation = historyStationId

  if (!targetStation || !historyCursor) {
    return
  }

    setHistoryLoading(true)
    setHistoryError(null)

    try {
      const response = await getHistory(targetStation, historyCursor)
      setHistoryItems((prev) => [...prev, ...response.items])
      setHistoryCursor(response.next_cursor ?? null)
      setHistoryHasMore(Boolean(response.next_cursor))
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to load more history.'
      setHistoryError(message)
    } finally {
      setHistoryLoading(false)
    }
  }, [historyStationId, historyCursor])

  const loadPlaylistHistoryInitial = React.useCallback(async () => {
    const targetStation = historyStationId

    if (!targetStation) {
      setPlaylistHistory([])
      return
    }

    setPlaylistHistoryLoading(true)
    setPlaylistHistoryError(null)

    try {
      const response = await getPlaylistHistory(targetStation)
      setPlaylistHistory(response.items)
      setPlaylistHistoryCursor(response.next_cursor ?? null)
      setPlaylistHistoryHasMore(Boolean(response.next_cursor))
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to load playlist history.'
      setPlaylistHistoryError(message)
      setPlaylistHistory([])
      setPlaylistHistoryCursor(null)
      setPlaylistHistoryHasMore(false)
    } finally {
      setPlaylistHistoryLoading(false)
    }
  }, [historyStationId])

  const loadPlaylistHistoryMore = React.useCallback(async () => {
    const targetStation = historyStationId

    if (!targetStation || !playlistHistoryCursor) {
      return
    }

    setPlaylistHistoryLoading(true)
    setPlaylistHistoryError(null)

    try {
      const response = await getPlaylistHistory(targetStation, playlistHistoryCursor)
      setPlaylistHistory((prev) => [...prev, ...response.items])
      setPlaylistHistoryCursor(response.next_cursor ?? null)
      setPlaylistHistoryHasMore(Boolean(response.next_cursor))
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to load more playlist history.'
      setPlaylistHistoryError(message)
    } finally {
      setPlaylistHistoryLoading(false)
    }
  }, [historyStationId, playlistHistoryCursor])

  const togglePlaylistExpansion = (jobId: number) => {
    setExpandedPlaylists((prev) => ({
      ...prev,
      [jobId]: !prev[jobId],
    }))
  }

  const parseHistoryTimestamp = (value?: string | null): number => {
    if (!value) {
      return 0
    }

    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  const combinedHistory = React.useMemo(() => {
    const rows: CombinedHistoryRow[] = [
      ...historyItems.map((entry) => ({
        kind: 'single',
        entry,
        timestamp: parseHistoryTimestamp(entry.created_at),
      })),
      ...playlistHistory.map((job) => ({
        kind: 'playlist',
        job,
        timestamp: parseHistoryTimestamp(job.created_at),
      })),
    ]

    return rows.sort((a, b) => b.timestamp - a.timestamp)
  }, [historyItems, playlistHistory])

  React.useEffect(() => {
    if (!stationId || !playlistJobId) {
      return
    }

    let cancelled = false
    let timer: number | null = null

    const poll = async () => {
      try {
        const status = await pollPlaylistJob(stationId, playlistJobId)
        if (cancelled) {
          return
        }
        setPlaylistJobStatus(status)
        if (status.status !== 'completed') {
          timer = window.setTimeout(poll, 3000)
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to poll playlist job.'
          setPlaylistJobError(message)
          timer = window.setTimeout(poll, 5000)
        }
      }
    }

    poll()

    return () => {
      cancelled = true
      if (timer) {
        window.clearTimeout(timer)
      }
    }
  }, [playlistJobId, stationId])

  React.useEffect(() => {
    if (playlistJobStatus?.status === 'completed' || playlistJobStatus?.status === 'failed') {
      loadPlaylistHistoryInitial()
    }
  }, [playlistJobStatus?.status, loadPlaylistHistoryInitial])

  React.useEffect(() => {
    if (!historyStationId) {
      setHistoryItems([])
      return
    }

    loadHistoryInitial()
  }, [historyStationId, loadHistoryInitial])

  React.useEffect(() => {
    if (!historyStationId) {
      setPlaylistHistory([])
      return
    }

    loadPlaylistHistoryInitial()
  }, [historyStationId, loadPlaylistHistoryInitial])

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    setTab(value)
  }

  const disabledReason = React.useMemo(() => {
    if (!stationId) {
      return 'No active station selected. Pick one from the main station menu first.'
    }
    if (!url.trim()) {
      return 'Paste a valid YouTube URL.'
    }
    return null
  }, [stationId, url])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!stationId || !url.trim()) {
      return
    }
    setSubmitting(true)
    setError(null)
    setResult(null)

    try {
      const payload = {
        station_id: stationId,
        url: url.trim(),
        bitrate: calculatedBitrate,
      }
      const response = await convertSingleVideo(payload)
      appendManualHistoryEntry(response.conversion)
      setStoredHistoryStationId(stationId)
      setResult(response)
      setCreditsStatus((prev) =>
        prev
          ? {
              ...prev,
              used: response.credits.used,
              remaining: response.credits.remaining,
              allowance: response.credits.allowance,
              window_started_at: response.credits.period_start_utc,
              resets_at: response.credits.resets_at,
            }
          : prev,
      )
      await loadHistoryInitial()
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to convert this video. Please try again.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFetchPlaylist = async () => {
    if (!stationId) {
      setPlaylistError('No active station selected. Choose one from the station menu.')
      return
    }
    if (!playlistUrl.trim()) {
      setPlaylistError('Paste a YouTube playlist URL.')
      return
    }

    setPlaylistLoading(true)
    setPlaylistError(null)
    setPlaylistJobId(null)
    setPlaylistJobStatus(null)
    setPlaylistJobError(null)
    try {
      const data = await fetchPlaylist({
        station_id: stationId,
        url: playlistUrl.trim(),
        bitrate: calculatedBitrate,
      })
      const defaults: Record<string, boolean> = {}
      data.items.forEach((item) => {
        defaults[item.video_id] = !item.over_limit
      })
      setPlaylistSelections(defaults)
      setPlaylistData(data)
      setPlaylistModalOpen(true)
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to inspect this playlist.'
      setPlaylistError(message)
    } finally {
      setPlaylistLoading(false)
    }
  }

  const handleClosePlaylistModal = () => {
    setPlaylistModalOpen(false)
    setPlaylistData(null)
    setPlaylistSelections({})
    setPlaylistProcessing(false)
    setPlaylistJobError(null)
  }

  const handlePlaylistStart = async () => {
    if (!stationId || !playlistData) {
      return
    }
    const selectedIds = Object.entries(playlistSelections)
      .filter(([, enabled]) => enabled)
      .map(([videoId]) => videoId)

    if (!selectedIds.length) {
      setPlaylistJobError('Select at least one eligible track.')
      return
    }

    setPlaylistProcessing(true)
    setPlaylistJobError(null)

    try {
      const payloadItems = playlistData.items
        .filter((item) => selectedIds.includes(item.video_id))
        .map((item) => ({
          video_id: item.video_id,
          video_title: item.title ?? '',
          duration_sec: item.duration_sec ?? null,
          estimated_size_bytes: item.estimated_size_bytes ?? null,
        }))

      const response = await startPlaylistJob({
        station_id: stationId,
        url: playlistData.playlist.url,
        playlist_id: playlistData.playlist.id,
        playlist_title: playlistData.playlist.title,
        bitrate: calculatedBitrate,
        items: payloadItems,
      })
      const trackThumbs: Record<string, string | null> = {}
      playlistData.items.forEach((item) => {
        if (selectedIds.includes(item.video_id)) {
          trackThumbs[item.video_id] = item.thumb_url ?? null
        }
      })
      setJobTrackThumbs(trackThumbs)
      setJobPlaylistInfo(playlistData.playlist)
      setPlaylistJobId(response.job_id)
      setPlaylistJobStatus({
        job_id: response.job_id,
        playlist_title: playlistData.playlist.title ?? null,
        status: 'pending',
        items: [],
      })
      await loadPlaylistHistoryInitial()
      handleClosePlaylistModal()
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Unable to start playlist download.'
      setPlaylistJobError(message)
    } finally {
      setPlaylistProcessing(false)
    }
  }

  const handleTogglePlaylistItem = (videoId: string) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setPlaylistSelections((prev) => ({
      ...prev,
      [videoId]: checked,
    }))
  }

  const handleSelectAllEligible = () => {
    if (!playlistData) {
      return
    }
    const next: Record<string, boolean> = {}
    playlistData.items.forEach((item) => {
      next[item.video_id] = !item.over_limit
    })
    setPlaylistSelections(next)
  }

  const handleDeselectAll = () => {
    if (!playlistData) {
      return
    }
    const next: Record<string, boolean> = {}
    playlistData.items.forEach((item) => {
      next[item.video_id] = false
    })
    setPlaylistSelections(next)
  }

  const renderSingleForm = () => (
    <Paper component="form" onSubmit={handleSubmit} className="rpa-ytmp3__card">
      <Stack spacing={3}>

        <TextField
          label="YouTube URL"
          placeholder="https://www.youtube.com/watch?v=example"
          required
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          fullWidth
        />

        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} className="rpa-ytmp3__actions" alignItems="center">
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={Boolean(disabledReason) || submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <DownloadRoundedIcon />}
            >
              {submitting ? 'Converting…' : 'Download MP3'}
            </Button>
            <CreditBadge status={creditsStatus} loading={creditsLoading} />
          </Stack>
          <FormHelperText sx={{ mt: 1 }}>
            Maximum file size:&nbsp;
            <strong>{maxFileSizeMb} MB</strong>. Larger tracks are blocked automatically.
          </FormHelperText>
          {disabledReason && (
            <FormHelperText error sx={{ mt: 1 }}>
              {disabledReason}
            </FormHelperText>
          )}
        </Box>

        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          >
            {error}
          </Alert>
        )}
      </Stack>
    </Paper>
  )

  const renderPlaylistPanel = () => (
    <Paper className="rpa-ytmp3__card">
      <Stack spacing={3}>
        <Typography variant="h6">Batch download a YouTube playlist</Typography>
        <TextField
          label="Playlist URL"
          placeholder="https://www.youtube.com/playlist?list=..."
          value={playlistUrl}
          onChange={(event) => setPlaylistUrl(event.target.value)}
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            startIcon={playlistLoading ? <CircularProgress size={20} /> : <QueueMusicRoundedIcon />}
            disabled={playlistLoading}
            onClick={handleFetchPlaylist}
          >
            {playlistLoading ? 'Inspecting…' : 'Inspect playlist'}
          </Button>
          <Typography color="text.secondary" variant="body2">
            We’ll auto-deselect any tracks that exceed the {maxFileSizeMb} MB limit.
          </Typography>
        </Stack>
        {playlistError && <FormHelperText error>{playlistError}</FormHelperText>}
      </Stack>
    </Paper>
  )

  const renderPlaylistProgressCard = () => {
    if (!playlistJobStatus) {
      return null
    }

    const processedCount = playlistJobStatus.items.filter((item) => item.status === 'success').length
    const totalCount = playlistJobStatus.items.length
    const playlistLabel =
      jobPlaylistInfo?.title ?? playlistJobStatus.playlist_title ?? `Playlist job ${playlistJobStatus.job_id}`

    return (
      <Paper className="rpa-ytmp3__card">
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                variant="rounded"
                src={jobPlaylistInfo?.thumb_url ?? undefined}
                alt={playlistLabel}
                sx={{ width: 48, height: 48 }}
              >
                {!jobPlaylistInfo?.thumb_url && <LibraryMusicRoundedIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6">{playlistLabel}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Job #{playlistJobStatus.job_id}
                </Typography>
              </Box>
            </Stack>
            <Chip size="small" color={getPlaylistJobColor(playlistJobStatus.status)} label={playlistJobStatus.status} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {playlistJobStatus.status !== 'completed' && playlistJobStatus.status !== 'failed' && (
              <CircularProgress size={20} />
            )}
            <Typography variant="body2" color="text.secondary">
              {processedCount}/{totalCount || '...'} tracks completed. Downloads continue in the background.
            </Typography>
          </Stack>
          {playlistJobStatus.error && <Alert severity="error">{playlistJobStatus.error}</Alert>}
          {playlistJobStatus.zip_url && (
            <Button
              size="small"
              variant="outlined"
              href={playlistJobStatus.zip_url}
              target="_blank"
              rel="noreferrer"
            >
              Download playlist ZIP
            </Button>
          )}
          {totalCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Waiting for playlist progress to be recorded.
            </Typography>
          ) : (
            <List dense>
              {playlistJobStatus.items.map((item) => (
                <ListItem key={`${playlistJobStatus.job_id}-${item.video_id}`} divider>
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={jobTrackThumbs[item.video_id] ?? undefined}
                      alt={item.title ?? item.video_id}
                      sx={{ width: 56, height: 56 }}
                    >
                      {!jobTrackThumbs[item.video_id] && <LibraryMusicRoundedIcon />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.title ?? item.video_id}
                    primaryTypographyProps={{ variant: 'subtitle2' }}
                    secondary={item.message ?? item.status}
                  />
                  <ListItemSecondaryAction>
                    <Stack spacing={0.5} alignItems="flex-end">
                      {item.status === 'success' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={item.cached ? 'info' : 'success'}
                          label={item.cached ? 'Cached' : 'Downloaded'}
                        />
                      )}
                      {item.file_url ? (
                        <Button
                          size="small"
                          variant="outlined"
                          href={item.file_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Download
                        </Button>
                      ) : (
                        <Chip
                          size="small"
                          color={item.status === 'success' ? 'success' : item.status === 'error' ? 'error' : 'info'}
                          label={item.status}
                        />
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
          <Button size="small" onClick={() => setPlaylistModalOpen(true)}>
            View playlist modal
          </Button>
        </Stack>
      </Paper>
    )
  }

  const renderHistorySection = () => (
    <Paper className="rpa-ytmp3__card">
      <Stack spacing={2}>
        {historyError && <Alert severity="warning">{historyError}</Alert>}
        {playlistHistoryError && <Alert severity="warning">{playlistHistoryError}</Alert>}
        {combinedHistory.length === 0 && !historyLoading && !playlistHistoryLoading ? (
          <Typography color="text.secondary">No downloads recorded for this station yet.</Typography>
        ) : (
          <List dense>
            {combinedHistory.map((row) => {
              if (row.kind === 'single') {
                const entry = row.entry
                return (
                  <ListItem key={`history-${entry.id}`} divider>
                    <ListItemIcon>
                      <LibraryMusicRoundedIcon color={entry.outcome === 'success' ? 'info' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={decodeHtmlEntities(entry.title ?? entry.video_id)}
                      secondary={
                        <Typography component="span" variant="body2" color="text.secondary">
                          {formatDateTime(entry.created_at)} ·{' '}
                          {entry.user?.name ? `by ${entry.user.name}` : 'User unknown'} ·{' '}
                          {formatDuration(entry.duration_sec)} ·{' '}
                          {formatMegabytes(entry.actual_size_bytes ?? entry.estimated_size_bytes)}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          color={entry.outcome === 'success' ? 'success' : entry.outcome === 'cached' ? 'info' : 'warning'}
                          label={entry.outcome}
                        />
                        {entry.file_url && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              href={entry.file_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Play
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              component="a"
                              href={entry.file_url}
                              download={buildDownloadFilename({ title: entry.title, id: entry.video_id })}
                              rel="noreferrer"
                            >
                              Download
                            </Button>
                          </>
                        )}
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              }

              const job = row.job
              const expanded = Boolean(expandedPlaylists[job.job_id])

              return (
                <React.Fragment key={`playlist-${job.job_id}`}>
                  <ListItem divider>
                    <ListItemAvatar>
                      <Avatar variant="rounded">
                        <LibraryMusicRoundedIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={job.playlist_title || job.playlist_id || `Playlist job ${job.job_id}`}
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(job.created_at)} · {job.items.length} tracks
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" color={getPlaylistJobColor(job.status)} label={job.status} />
                        {job.zip_url && (
                          <Button
                            size="small"
                            variant="outlined"
                            href={job.zip_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Download ZIP
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => togglePlaylistExpansion(job.job_id)}
                          aria-label={expanded ? 'Collapse tracks' : 'Expand tracks'}
                        >
                          {expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                        </IconButton>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List dense disablePadding>
                      {job.items.map((item) => {
                        const cached = Boolean(item.cached)
                        const success = item.status === 'success'

                        return (
                          <ListItem
                            key={`playlist-${job.job_id}-${item.video_id}`}
                            divider
                            sx={{ pl: 6 }}
                          >
                            <ListItemAvatar>
                              <Avatar variant="rounded">
                                <LibraryMusicRoundedIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={item.title ?? item.video_id}
                              primaryTypographyProps={{ variant: 'subtitle2' }}
                              secondary={
                                <Typography variant="body2" color="text.secondary">
                                  {item.message ?? (success ? (cached ? 'Cached' : 'Downloaded') : item.status)}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Stack spacing={0.5} alignItems="flex-end">
                                {success && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color={cached ? 'info' : 'success'}
                                    label={cached ? 'Cached' : 'Downloaded'}
                                  />
                                )}
                                {item.file_url ? (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      href={item.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Play
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      component="a"
                                      href={item.file_url}
                                      download={buildDownloadFilename({ title: item.title, id: item.video_id })}
                                      rel="noreferrer"
                                    >
                                      Download
                                    </Button>
                                  </>
                                ) : (
                                  !success && (
                                    <Chip
                                      size="small"
                                      color={
                                        item.status === 'error'
                                          ? 'error'
                                          : item.status === 'success'
                                          ? 'success'
                                          : 'info'
                                      }
                                      label={item.status}
                                    />
                                  )
                                )}
                              </Stack>
                            </ListItemSecondaryAction>
                          </ListItem>
                        )
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              )
            })}
          </List>
        )}
        {(historyLoading || playlistHistoryLoading) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {historyHasMore && !historyLoading && (
            <Button variant="outlined" onClick={loadHistoryMore}>
              Load more
            </Button>
          )}
          {playlistHistoryHasMore && !playlistHistoryLoading && (
            <Button variant="outlined" onClick={loadPlaylistHistoryMore}>
              Load more playlist history
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  )



  if (!stations.length) {
    return (
      <Stack className="rpa-ytmp3" spacing={3} sx={{ flexGrow: 1, pb: { xs: 4, md: 6 } }}>
        <Alert severity="warning">You need access to a station before using the downloader.</Alert>
      </Stack>
    )
  }

  const selectedCount = Object.values(playlistSelections).filter(Boolean).length
  const eligibleCount = playlistData ? playlistData.items.filter((item) => !item.over_limit).length : 0

  return (
    <Stack className="rpa-ytmp3" spacing={3} sx={{ flexGrow: 1, pb: { xs: 4, md: 6 } }}>
      <Tabs value={tab} onChange={handleTabChange} aria-label="Downloader mode">
        <Tab label="Single video" value="single" />
        <Tab label="Playlist batch" value="playlist" />
      </Tabs>

      {renderPlaylistProgressCard()}
      {tab === 'single' && renderSingleForm()}
      {tab === 'single' && result?.conversion?.file_url && (
        <a
          ref={downloadAnchorRef}
          href={result.conversion.file_url}
          target="_blank"
          rel="noreferrer"
          download={singleDownloadFilename}
          style={{ display: 'none' }}
        />
      )}
      {tab === 'playlist' && renderPlaylistPanel()}

      {tab === 'single' && renderHistorySection()}

      <Dialog open={playlistModalOpen} onClose={handleClosePlaylistModal} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          <Avatar
            variant="rounded"
            src={playlistData?.playlist.thumb_url ?? undefined}
            alt={playlistData?.playlist.title ?? 'Playlist thumbnail'}
            sx={{ width: 64, height: 64 }}
          >
            {!playlistData?.playlist.thumb_url && <LibraryMusicRoundedIcon />}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">
              {playlistData?.playlist.title ?? 'Playlist overview'} ({playlistData?.items.length ?? 0} tracks)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Station: {creditsStatus?.station_name ?? activeStation?.name ?? '—'}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClosePlaylistModal}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
        <DialogContent dividers>
          {playlistData ? (
            <Stack spacing={2}>
              {eligibleCount === 0 && (
                <Alert severity="warning" icon={<DoDisturbRoundedIcon />}>
                  Every track in this playlist exceeds the {maxFileSizeMb} MB limit.
                </Alert>
              )}
              <List dense>
                {playlistData.items.map((item) => {
                  const disabled = Boolean(item.over_limit)
                  const checked = playlistSelections[item.video_id] ?? !disabled
                  return (
                    <ListItem
                      key={item.video_id}
                      divider
                      secondaryAction={
                        disabled ? (
                          <Chip color="warning" size="small" label="Over limit" />
                        ) : (
                          <Chip size="small" label={item.duration_sec ? `${item.duration_sec}s` : 'Ready'} />
                        )
                      }
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          disabled={disabled || Boolean(playlistJobId)}
                          onChange={handleTogglePlaylistItem(item.video_id)}
                        />
                      </ListItemIcon>
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={item.thumb_url ?? undefined}
                          alt={item.title ?? item.video_id}
                          sx={{ width: 64, height: 64 }}
                        >
                          {!item.thumb_url && <LibraryMusicRoundedIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.title ?? item.video_id}
                        primaryTypographyProps={{ variant: 'subtitle1' }}
                        secondary={
                          item.estimated_size_bytes
                            ? `Estimated size ${formatBytes(item.estimated_size_bytes)}`
                            : 'Size unavailable'
                        }
                      />
                    </ListItem>
                  )
                })}
              </List>
              {playlistJobError && <Alert severity="error">{playlistJobError}</Alert>}
              {playlistJobStatus && (
                <Stack spacing={1}>
                  <Alert severity={playlistJobStatus.status === 'completed' ? 'success' : 'info'}>
                    Playlist job {playlistJobStatus.status}. We’ll refresh every few seconds.
                  </Alert>
                  <List dense>
                    {playlistJobStatus.items.map((item) => (
                      <ListItem key={item.video_id} divider>
                        <ListItemText
                          primary={item.title ?? item.video_id}
                          secondary={item.message ?? item.status}
                        />
                        <ListItemSecondaryAction>
                          {item.file_url ? (
                            <Button
                              size="small"
                              variant="outlined"
                              href={item.file_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Download
                            </Button>
                          ) : (
                            <Chip
                              size="small"
                              color={item.status === 'success' ? 'success' : item.status === 'error' ? 'error' : 'default'}
                              label={item.status}
                            />
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              )}
            </Stack>
          ) : (
            <Typography color="text.secondary">Paste a playlist URL to inspect eligible tracks.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAllEligible}
              disabled={!playlistData || Boolean(playlistJobId)}
            >
              Select all eligible ({eligibleCount})
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDeselectAll}
              disabled={!playlistData || Boolean(playlistJobId)}
            >
              Deselect all
            </Button>
            <Typography variant="body2" color="text.secondary">
              {selectedCount} selected
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClosePlaylistModal}>Close</Button>
            <Button
              onClick={handlePlaylistStart}
              disabled={!playlistData || selectedCount === 0 || Boolean(playlistJobId) || playlistProcessing}
              startIcon={playlistProcessing ? <CircularProgress size={20} /> : <DownloadRoundedIcon />}
            >
              {playlistProcessing ? 'Starting…' : 'Start downloads'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

interface CreditBadgeProps {
  status: YtCreditsStatus | null
  loading: boolean
}

const CreditBadge: React.FC<CreditBadgeProps> = ({ status, loading }) => {
  if (loading) {
    return <Chip label="Loading credits…" variant="outlined" color="default" />
  }

  if (!status) {
    return <Chip label="Credits unavailable" variant="outlined" />
  }

  const remaining = status.remaining ?? Infinity
  const label =
    remaining === Infinity ? 'Unlimited credits' : `${remaining} credit${remaining === 1 ? '' : 's'} left`
  const resetsText = formatResetTime(status.resets_at)

  return (
    <Chip
      color={remaining !== Infinity && remaining <= 1 ? 'warning' : 'default'}
      label={`${label} · resets ${resetsText}`}
    />
  )
}

function formatResetTime(resetsAt: string): string {
  const date = new Date(resetsAt)

  if (Number.isNaN(date.getTime())) {
    return 'soon'
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Madrid',
  })

  return formatter.format(date)
}

function formatBytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) {
    return 'N/A'
  }
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex++
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return 'Just now'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Just now'
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return formatter.format(date)
}

function formatDuration(seconds?: number | null): string {
  if (!seconds || seconds <= 0) {
    return 'Duration n/a'
  }

  const totalSeconds = Math.round(seconds)
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`
}

function formatMegabytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) {
    return 'Size n/a'
  }

  const megabytes = bytes / 1024 / 1024
  return `${megabytes.toFixed(1)} MB`
}

function decodeHtmlEntities(value?: string | null): string {
  if (!value) {
    return ''
  }

  if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = value
    return textarea.value
  }

  const namedEntities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&#8211;': '–',
    '&#8217;': '’',
    '&#8220;': '“',
    '&#8221;': '”',
    '&#038;': '&',
    '&#8212;': '—',
    '&#8216;': '‘',
    '&#8230;': '…',
  }

  let decoded = value
  for (const [entity, replacement] of Object.entries(namedEntities)) {
    decoded = decoded.replace(new RegExp(entity, 'g'), replacement)
  }

  decoded = decoded.replace(/&#(\d+);/g, (_, num) =>
    Number.isNaN(Number(num)) ? '' : String.fromCharCode(Number(num)),
  )
  decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
    Number.isNaN(Number.parseInt(hex, 16)) ? '' : String.fromCharCode(Number.parseInt(hex, 16)),
  )

  return decoded
}

type DownloadSource = {
  title?: string | null
  id?: string | number | null
}

function buildDownloadFilename(source: DownloadSource): string {
  const decodedTitle = decodeHtmlEntities(source.title ?? '')
  const baseTitle = decodedTitle.trim()
  const fallbackId = source.id !== undefined && source.id !== null ? String(source.id) : ''
  const sourceValue = baseTitle && baseTitle !== '' ? baseTitle : fallbackId || 'yt-mp3'
const cleaned = sourceValue
    .replace(/[\u0000-\u001f]/g, '')
    .replace(/[<>:"/\\|?*]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (cleaned === '') {
    return 'yt-mp3.mp3'
  }

  return cleaned.toLowerCase().endsWith('.mp3') ? cleaned : `${cleaned}.mp3`
}

function getPlaylistJobColor(status: PlaylistHistoryJob['status']): 'default' | 'info' | 'success' | 'warning' | 'error' {
  if (status === 'completed') {
    return 'success'
  }

  if (status === 'failed') {
    return 'error'
  }

  return 'info'
}

export default YoutubeMp3Downloader
